import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
  IconButton,
  Fade,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, LockOutlined } from '@mui/icons-material';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../api';
import AppFooter, { APP_FOOTER_RESERVE_PX } from '../components/AppFooter';

const Login = ({ variant }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDriverEntry = variant === 'driver';
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const key = searchParams.get('key');
    if (!isDriverEntry || !key) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}public/portal-key/${encodeURIComponent(key)}`);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && data.email) {
          setFormData((prev) => ({ ...prev, email: data.email }));
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, isDriverEntry]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(formData),
      });

      const raw = await response.text();
      let data;
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        setError(raw ? raw.slice(0, 240) : `Server error (${response.status})`);
        return;
      }

      if (response.ok && (data.success === true || data.status === 'success')) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        const role = data.user.role;
        if (['super_admin', 'admin', 'office_admin'].includes(role)) {
          navigate('/');
        } else if (role === 'manager') {
          navigate('/jobs');
        } else {
          navigate('/drivers/portal');
        }
      } else {
        const msg =
          data.message ||
          (data.errors && typeof data.errors === 'object'
            ? Object.values(data.errors).flat().join(' ')
            : null);
        setError(msg || 'Login failed. Please try again.');
      }
    } catch (error) {
      const msg =
        error instanceof Error && error.message && !error.message.includes('fetch')
          ? error.message
          : 'Network error. Please check your connection.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'radial-gradient(ellipse at bottom, #111827 0%, #0B1121 100%)',
        position: 'relative',
        overflowX: 'hidden',
        paddingBottom: `${APP_FOOTER_RESERVE_PX}px`,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
          py: 2,
        }}
      >
      {/* Animated Floating Bubbles */}
      <Box sx={{
        position: 'absolute', top: '10%', left: '20%', width: '300px', height: '300px',
        background: 'radial-gradient(circle, rgba(79,70,229,0.15) 0%, transparent 70%)',
        filter: 'blur(40px)', animation: 'pulse 6s infinite alternate',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '10%', right: '15%', width: '400px', height: '400px',
        background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
        filter: 'blur(50px)', animation: 'pulse 8s infinite alternate-reverse',
      }} />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
        <Fade in timeout={800}>
          <Box
            sx={{
              p: { xs: 4, sm: 6 },
              borderRadius: '24px',
              background: 'rgba(30, 41, 59, 0.4)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              boxShadow: '0 24px 64px -12px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255,255,255,0.1)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                width: 64, height: 64, mb: 3, borderRadius: '16px',
                background: 'linear-gradient(135deg, #4F46E5 0%, #8B5CF6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#ffffff', fontSize: '32px', fontWeight: 800,
                boxShadow: '0 8px 32px rgba(79, 70, 229, 0.4), inset 0 2px 0 rgba(255,255,255,0.2)',
              }}
            >
              W
            </Box>

            <Typography variant="h3" sx={{ fontWeight: 800, color: '#f8fafc', mb: 1, letterSpacing: '-0.02em', textAlign: 'center' }}>
              {isDriverEntry ? 'Driver Portal' : 'Welcome back'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', mb: 4, textAlign: 'center' }}>
              {isDriverEntry
                ? 'Sign in with your email and password to see only your tasks.'
                : 'Enter your credentials to access your workspace.'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: '12px', background: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)', '& .MuiAlert-icon': { color: '#fca5a5' } }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                fullWidth
                placeholder="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                variant="outlined"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Email sx={{ color: '#64748b' }} /></InputAdornment>,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#f8fafc',
                    background: 'rgba(15, 23, 42, 0.6)',
                    borderRadius: '12px',
                    transition: 'all 0.3s',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                    '&.Mui-focused fieldset': { borderColor: '#8B5CF6', borderWidth: '2px' },
                    '&.Mui-focused': {
                      background: 'rgba(15, 23, 42, 0.8)',
                      boxShadow: '0 0 0 4px rgba(139, 92, 246, 0.1)'
                    }
                  },
                }}
              />

              <TextField
                fullWidth
                placeholder="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                variant="outlined"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: '#64748b' }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: '#64748b', '&:hover': { color: '#f8fafc' } }}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#f8fafc',
                    background: 'rgba(15, 23, 42, 0.6)',
                    borderRadius: '12px',
                    transition: 'all 0.3s',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                    '&.Mui-focused fieldset': { borderColor: '#8B5CF6', borderWidth: '2px' },
                    '&.Mui-focused': {
                      background: 'rgba(15, 23, 42, 0.8)',
                      boxShadow: '0 0 0 4px rgba(139, 92, 246, 0.1)'
                    }
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 1,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #4F46E5 0%, #8B5CF6 100%)',
                  color: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(79, 70, 229, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4338CA 0%, #7C3AED 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(79, 70, 229, 0.4)',
                  },
                  '&:disabled': {
                    background: 'rgba(79, 70, 229, 0.5)',
                  }
                }}
              >
                {loading ? <CircularProgress size={26} sx={{ color: 'white' }} /> : 'Log In'}
              </Button>
            </Box>

            {!isDriverEntry && (
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 2, textAlign: 'center' }}>
                <Link component={RouterLink} to="/register" color="inherit" underline="hover">
                  Register
                </Link>
              </Typography>
            )}

            {/* removed default super admin hint on production login */}
          </Box>
        </Fade>
      </Container>
      </Box>
      <AppFooter variant="dark" />
    </Box>
  );
};

export default Login;
